import {
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { createBusinessContactThunk } from './thunks/createBusinessContact.thunk';
import { getBusinessContactsThunk } from './thunks/getBusinessContacts.thunk';

export { createBusinessContactThunk, getBusinessContactsThunk };

const contactsAdapter = createEntityAdapter();

export const contactsSlice = createSlice({
  name: 'contacts',
  initialState: {
    contacts: contactsAdapter.getInitialState(),
    loadedForBusinessId: null,
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(createBusinessContactThunk.fulfilled, (state, action) => {
        if (action.payload) {
          contactsAdapter.upsertOne(state.contacts, action.payload);
        }
      })
      .addCase(getBusinessContactsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBusinessContactsThunk.fulfilled, (state, action) => {
        const contacts = action.payload ?? [];
        contactsAdapter.setAll(state.contacts, contacts);
        state.loading = false;
      })
      .addCase(getBusinessContactsThunk.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });
  },
});

export const contactsAdapterSelectors = contactsAdapter.getSelectors(
  (state) => state.contacts.contacts
);

export const selectContactsLoading = (state) => state.contacts.loading;
export const selectContactsLoadedForBusinessId = (state) =>
  state.contacts.loadedForBusinessId;

export const selectContactsWithEmail = createSelector(
  contactsAdapterSelectors.selectAll,
  (contacts) => contacts.filter((c) => !!c.emailAddress)
);

// The companies the contact book can be filtered by. Derived from the contacts
// themselves rather than fetched: only contacts traceable to an employer carry
// one, so this is exactly the set that filters to a non-empty result. A company
// whose name never resolved server-side is still listed, under its business id
// alone. Sorted by name, with name-less companies last.
export const selectContactCompanies = createSelector(
  contactsAdapterSelectors.selectAll,
  (contacts) => {
    const byBusinessId = new Map();

    for (const contact of contacts) {
      const businessId = contact.companyBusinessId;
      if (!businessId) continue;

      const existing = byBusinessId.get(businessId);
      if (!existing) {
        byBusinessId.set(businessId, { businessId, name: contact.companyName });
      } else if (!existing.name && contact.companyName) {
        existing.name = contact.companyName;
      }
    }

    return [...byBusinessId.values()].sort((a, b) => {
      if (!a.name) return b.name ? 1 : a.businessId.localeCompare(b.businessId);
      if (!b.name) return -1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }
);
